import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../components/Navbar';
import ShipmentsMap from '../components/ShipmentsMap';
import AddWaypointsModal from '../components/AddWaypointsModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchDeliveryDateShipments, addShipments } from '../utils/api';

export default function RoutePlanningScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get date from URL query parameter
  const dateFromUrl = searchParams.get('date');
  
  
  // Use URL date with fallback to state date
  const [selectedDate] = useState(dateFromUrl);
  const [shipments, setShipments] = useState([]);
  const [isLoading, setIsLoading] = useState();
  const [loadError, setLoadError] = useState(null);
  const [stats, setStats] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentShipmentId, setCurrentShipmentId] = useState('');
  const [addedShipmentIds, setAddedShipmentIds] = useState([]);
  const [isAddingShipments, setIsAddingShipments] = useState(false);
  const [isWaypointsModalOpen, setIsWaypointsModalOpen] = useState(false);
  const [searchShipmentNumber, setSearchShipmentNumber] = useState('');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const mapRef = useRef(null);

  // Load shipments data on component mount if we have a date but no shipments
  useEffect(() => {
    loadShipmentsData();
  }, [selectedDate]); // Remove 'shipments' from the dependency array to prevent loops

  async function loadShipmentsData() {
    if (!selectedDate) {
      return;
    }
    
    setIsLoading(true);
    setLoadError(null);

    try {
      console.log(`Loading shipments data for date: ${selectedDate}`);
      const shipmentsData = await fetchDeliveryDateShipments(selectedDate);
      
      if (shipmentsData && shipmentsData.length > 0) {
        console.log(`Successfully loaded ${shipmentsData.length} shipments`);
        setShipments(shipmentsData);
      } else {
        console.log(`No shipments found for ${selectedDate}`);
      }
    } catch (error) {
      console.error('Error loading shipments:', error);
      setLoadError(`Failed to load shipments: ${error.message}`);
      toast.error(`Failed to load shipments: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }
        
  // Update URL when date changes to maintain state on reload
  useEffect(() => {
    if (selectedDate && !dateFromUrl) {
      // Update URL without reloading the page
      navigate(`/route-planning?date=${selectedDate}`, { replace: true });
    }
  }, [selectedDate, dateFromUrl, navigate]);

  const handleStatsUpdate = (newStats) => {
    console.log('Received stats update:', newStats);
    setStats(newStats);
  };

  const handleShipmentsUpdate = (updatedShipments) => {
    setShipments(updatedShipments);
  };

  // Function to handle map reference from ShipmentsMap component
  const handleMapReady = (mapReference) => {
    mapRef.current = mapReference;
  };

  // Function to search for shipment and display its popup
  const handleShipmentSearch = (e) => {
    e.preventDefault();
    
    if (!searchShipmentNumber || !shipments.length) return;
    
    // First, reset the selected shipment to force popup to reopen even if it's the same shipment
    setSelectedShipment(null);
    
    // Use a small timeout to ensure React has time to process the state change
    setTimeout(() => {
      const searchText = searchShipmentNumber.trim();
      // Find shipments where the number contains the search text (case insensitive)
      const foundShipments = shipments.filter(shipment => 
        shipment.number.toLowerCase().includes(searchText.toLowerCase())
      );
      
      if (foundShipments.length > 0) {
        // Select the first matching shipment
        setSelectedShipment(foundShipments[0]);
        
        // Show success toast with shipment info and count of matches
        if (foundShipments.length === 1) {
          toast.success(`Found shipment ${foundShipments[0].number}`);
        } else {
          toast.success(`Found ${foundShipments.length} shipments. Showing first match: ${foundShipments[0].number}`);
        }
        
      } else {
        toast.error(`No shipments found containing "${searchText}"`);
        setSelectedShipment(null);
      }
    }, 10);
  };

  const handleSaveRoutePlan = () => {
    // Format the date as YYYY-MM-DD for the URL parameter
    const formattedDate = selectedDate instanceof Date
      ? selectedDate.toISOString().split('T')[0]
      : selectedDate;

    // Navigate to the delivery clustering screen with the delivery_date parameter
    navigate(`/delivery-clustering?delivery_date=${formattedDate}`);
  };

  const openAddShipmentsModal = () => {
    setIsModalOpen(true);
    setAddedShipmentIds([]);
    setCurrentShipmentId('');
    setIsAddingShipments(false);
  };

  const closeModal = () => {
    if (!isAddingShipments) {
      setIsModalOpen(false);
      setCurrentShipmentId('');
      setAddedShipmentIds([]);
    }
  };

  const handleAddToList = () => {
    if (currentShipmentId.trim()) {
      // Avoid duplicates
      if (!addedShipmentIds.includes(currentShipmentId.trim())) {
        setAddedShipmentIds([...addedShipmentIds, currentShipmentId.trim()]);
      }
      setCurrentShipmentId('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddToList();
    }
  };

  const handleRemoveId = (idToRemove) => {
    setAddedShipmentIds(addedShipmentIds.filter(id => id !== idToRemove));
  };

  const handleSubmitIds = async () => {
    if (addedShipmentIds.length > 0) {
      try {
        setIsAddingShipments(true);
        const formattedDate = selectedDate instanceof Date
          ? selectedDate.toISOString().split('T')[0]
          : selectedDate;

        const result = await addShipments(formattedDate, addedShipmentIds);

        if (!result.success || result.data.not_found_shipments.length > 0) {
          console.error(`Shipments not found: ${result.data.not_found_shipments.join(', ')}`);
          toast.error(`Shipments not found: ${result.data.not_found_shipments.join(', ')}`);
          
          if (!result.success) {
            return;
          }

          const badShipments = result.data.not_found_shipments;
          setAddedShipmentIds(badShipments);
          return; 
        }

        toast.success('Shipments added successfully');
        closeModal();
        setIsAddingShipments(false);
        loadShipmentsData();
      } catch (e) {
        console.error('Error adding shipments:', e);
        toast.error('Failed to add shipments. Please try again.');
      } finally {
        setIsAddingShipments(false);
      }
    }
  };
  
  const handleWaypointsSubmit = async (waypoints) => {
    // Here you would typically send the waypoints to your backend
    console.log('Waypoints to save:', waypoints);
    // TODO: Implement the API call to save waypoints
    toast.success('Waypoints added successfully');
    loadShipmentsData();
  };

  const navigationButtons = (
    <>
      <button
        onClick={() => navigate('/')}
        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Back to Calendar
      </button>
      <button
        onClick={handleSaveRoutePlan}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Save Route Plan
      </button>
      <button
        onClick={openAddShipmentsModal}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Add Shipments
      </button>
      <button
        onClick={() => setIsWaypointsModalOpen(true)}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Add Waypoints
      </button>
    </>
  );

  // Show loading state if date is missing or redirect to home
  if (!selectedDate) {
    return navigate('/');
  }

  // Display loading indicator while fetching data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading shipments for {selectedDate}...</p>
        </div>
      </div>
    );
  }

  // Display error message if loading failed
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar buttons={navigationButtons} />
        <div className="flex h-[calc(100vh-4rem)] items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full">
            <h2 className="text-red-600 text-lg font-semibold mb-4">Error Loading Shipments</h2>
            <p className="text-gray-700 mb-4">{loadError}</p>
            <div className="flex justify-end">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Back to Calendar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar buttons={navigationButtons} />
      <div className="flex h-[calc(100vh-4rem)]">
        {/* Left side - Info panel */}
        <div className="w-64 bg-white shadow-lg p-4 overflow-auto">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Route Planning</h2>
          
          {/* Search shipment form */}
          <div className="mt-4 mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Search Shipment:</h3>
            <form onSubmit={handleShipmentSearch} className="flex flex-col space-y-2">
              <input
                type="text"
                value={searchShipmentNumber}
                onChange={(e) => setSearchShipmentNumber(e.target.value)}
                placeholder="Enter shipment number"
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
              <button
                type="submit"
                className="w-full px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Search
              </button>
            </form>
          </div>
          
          <div className="mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Date:</h3>
            <p className="text-sm text-gray-600">
              {new Date(selectedDate).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>

          {stats && (
            <>
              <div className="mt-4 p-2 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
                {stats.totalShipments} total shipments
              </div>

              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Delivery Summary:</h3>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    • {stats.npDeliveries} Nova Post deliveries
                  </p>
                  <p className="text-sm text-gray-600">
                    • {stats.partnerDeliveries} Partner deliveries
                  </p>
                  <p className="text-sm text-gray-600">
                    • {stats.selfSubmittedDeliveries || 0} Nova Post Pickups
                  </p>
                  <p className="text-sm text-gray-600">
                    • {stats.glsPickupDeliveries || 0} GLS Pickups
                  </p>
                  <p className="text-sm text-gray-600">
                    • {stats.otherDeliveries || 0} Other delivery types
                  </p>
                  <p className="text-sm text-gray-600">
                    • {stats.totalSelfPickups} self-pickups at {stats.selfPickupLocations} location{stats.selfPickupLocations !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right side - Map */}
        <div className="flex-1">
          <ShipmentsMap
            key={`shipments-map-date-${selectedDate}`}
            shipments={shipments}
            onStatsUpdate={handleStatsUpdate}
            onShipmentsUpdate={handleShipmentsUpdate}
            selectedDate={selectedDate}
            selectedShipment={selectedShipment}
            onMapReady={handleMapReady}
          />
        </div>
      </div>

      {/* Add Shipments Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex items-center justify-center p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative z-[1001]">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Add Shipments</h3>

            {isAddingShipments ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
                <p className="text-gray-600">Adding shipments to delivery date...</p>
              </div>
            ) : (
              <>
                {/* Input field and Add button */}
                <div className="mb-4">
                  <label htmlFor="shipmentId" className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Shipment ID
                  </label>
                  <div className="flex">
                    <input
                      type="text"
                      id="shipmentId"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      value={currentShipmentId}
                      onChange={(e) => setCurrentShipmentId(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="e.g. SHNL5846577755"
                    />
                    <button
                      type="button"
                      onClick={handleAddToList}
                      className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-r-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* List of added IDs */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shipment IDs to Add:
                  </label>
                  {addedShipmentIds.length > 0 ? (
                    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                      <ul className="divide-y divide-gray-200">
                        {addedShipmentIds.map((id, index) => (
                          <li key={index} className="py-2 flex justify-between items-center">
                            <span className="text-sm text-gray-800">{id}</span>
                            <button
                              onClick={() => handleRemoveId(id)}
                              className="text-red-500 hover:text-red-700"
                              title="Remove"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-gray-500 italic border border-gray-300 rounded-md p-4 text-center">
                      No shipment IDs added yet
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitIds}
                    disabled={addedShipmentIds.length === 0}
                    className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                      addedShipmentIds.length === 0 
                        ? 'bg-indigo-400 cursor-not-allowed' 
                        : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    Submit ({addedShipmentIds.length})
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Add Waypoints Modal */}
      <AddWaypointsModal
        isOpen={isWaypointsModalOpen}
        onClose={() => setIsWaypointsModalOpen(false)}
        onSubmit={handleWaypointsSubmit}
        currentDeliveryDate={searchParams.get('date')}
      />
    </div>
  );
}
